<template>
  <b-row v-if="company" class="mb-5">
    <b-col cols="12" md="10" offset-md="1">
      <b-card class="shadow px-1 py-2 mb-5">

        <onboarding-stepper
          v-if="currentStep.stepper"
          :currentStep="currentStep"
          :steps="steps"
        />

        <template v-if="isIntro">
          <b-row class="pt-5 pb-4 px-lg-5">
            <b-col cols="12" class="px-4 px-lg-5">
              <h2 v-if="templates.introTitle" class="text-center px-lg-5">{{ templates.introTitle }}</h2>
              <template v-else>
                <h3 class="text-center px-lg-5">Interested in making referrals easy?</h3>
                <h3 class="text-center px-lg-5">Match your networks to jobs automatically.</h3>
              </template>
            </b-col>
            <b-col
              v-if="company && company.branding && company.branding.onboardingRegisterNetworkImage && company.branding.onboardingRegisterNetworkImage.fileLocation"
              cols="12" md="6" lg="4" class="px-4 py-4 pl-lg-5"
            >
              <b-img
                :src="company.branding.onboardingRegisterNetworkImage.fileLocation"
                fluid
              ></b-img>
            </b-col>
            <b-col cols="12" md="6" lg="8" class="px-4 py-4 pr-lg-5">
              <p v-if="templates.introDescription">{{ templates.introDescription }}</p>
              <p v-else>
                <b>Match Alerts</b><br>If you connect your networks, we’ll match them to jobs and you’ll receive match alerts.<br><br>
                <b>Uncovering Talent </b><br>We’ll help uncover potential referrals in your wider network you might not have thought of.<br><br>
                <b>Rewards for Connecting</b><br>You’ll earn points for connecting networks to help you to win some of the great prizes.<br><br>
                <b>Control of Contacts</b><br>Only you can contact your connections and see their personal information.
              </p>
            </b-col>
          </b-row>

           <b-row class="px-5">
              <b-col class="w-100 px-4">
                <a class="float-left pt-2 text-secondary" href="#" @click.prevent="$emit('goBack')">Go back</a>
                <b-button @click.prevent="isIntro = false" class="float-right px-5" variant="primary">Continue</b-button>
                <b-button v-if="isIntro" class="float-right px-5 mr-3" variant="outline-primary" @click.prevent="() => $emit('goNext')">Skip</b-button>
              </b-col>
            </b-row>
        </template>

        <template v-else>
          <div class="my-3 text-center">
            <h3>{{ $t('onboarding.sync-connections.title') }}</h3>
            <h4>{{ $t('onboarding.sync-connections.description') }}</h4>
          </div>

          <b-alert v-if="!isEnglishLang" class="text-center" show variant="warning">
            {{ $t('onboarding.sync-connections.still-localising') }}
          </b-alert>

          <contacts-upload-promo class="my-5"/>

          <contacts-upload-container class="p-0 my-5" :preferences="company.networkPreferences"
            :preSelect="preSelectedUploadProvider" :redirectUriTemplate="redirectUriTemplate"
            @finished-uploading="onFinished" data-tour="upload-container"/>

          <contacts-upload-privacy/>

          <div class="alert alert-warning d-flex flex-row align-items-center px-3">
            <p class="text-muted m-0">
              <strong>{{ $t('onboarding.sync-connections.not-ready-title') }}</strong>
              {{ $t('onboarding.sync-connections.not-ready-description') }}
            </p>
          </div>

          <b-row>
            <b-col class="d-flex justify-content-between align-items-center px-3">
              <a class="text-secondary" href="#" @click.prevent="() => $emit('goBack')">{{ $t('ctas.go-back') }}</a>
              <b-button @click.prevent="() => $emit('goNext', { sync: hasSynced })" class="px-5" variant="primary">{{ $t('ctas.continue') }}</b-button>
            </b-col>
          </b-row>
        </template>

      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import RegisterNetworkQuery from '@/graphql/queries/RegisterNetwork.gql';
import ContactsUploadContainer from '@/components/contacts-upload/Container';
import ContactsUploadPromo from '@/components/contacts-upload/Promo';
import ContactsUploadPrivacy from '@/components/contacts-upload/Privacy';
import OnboardingStepper from '@/components/profile/OnboardingStepper';
import { mapRoot } from '@/mixins/root';

const templateMap = {
  ONBOARDING_MENTORSHIP_INTRO_TITLE: 'introTitle',
  ONBOARDING_MENTORSHIP_INTRO_SUBTITLE: 'introSubtitle',
  ONBOARDING_MENTORSHIP_INTRO_DESCRIPTION: 'introDescription',
  ONBOARDING_MENTORSHIP_TITLE: 'title',
  ONBOARDING_MENTORSHIP_DESCRIPTION: 'description',
};

export default {
  name: 'OnboardingContacts',
  components: {
    ContactsUploadContainer,
    ContactsUploadPromo,
    ContactsUploadPrivacy,
    OnboardingStepper,
  },
  props: {
    currentStep: {
      type: Object,
      default: () => null,
    },
    steps: {
      type: Array,
      default: () => [],
    }
  },
  data: () => ({
    fatalErr: null,
    hasSynced: false,
    isIntro: true,
  }),
  apollo: {
    company: {
      query: RegisterNetworkQuery,
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { sitePreferences } = data.company;
          const isInternalMobilityEnabled = sitePreferences ? sitePreferences.isInternalMobilityEnabled : false;
          this.isIntro = isInternalMobilityEnabled === true;
        }
      },
      error(err) {
        this.fatalErr = err;
      },
    },
  },
  created() {
    if (this.preSelectedUploadProvider) {
      this.isIntro = false;
    }
  },
  metaInfo() {
    return {
      title: this.$t('onboarding.sync-connections.title'),
    };
  },
  computed: {
    ...mapRoot([ 'isEnglishLang' ]),
    canOnlySyncOneNetwork() {
      const { networkPreferences } = this.company;
      const canSyncNetworks = Object.keys(networkPreferences)
        .filter(key => key.startsWith('canSyncContactsWith') && networkPreferences[key] === true);
      return canSyncNetworks.length === 1;
    },
    redirectUriTemplate() {
      return `${this.$root.origin}${this.$route.path}?provider=$PROVIDER`;
    },
    preSelectedUploadProvider() {
      if (this.$route && this.$route.query && this.$route.query.provider) {
        return this.$route.query.provider;
      } else {
        return null;
      }
    },
    templates() {
      const templates = {};
      if (this.company && Array.isArray(this.company.templatedStrings)) {
        const { templatedStrings } = this.company;
        Object.keys(templateMap).forEach(templateKey => {
          const template = templatedStrings.find(el => el.key === templateKey);
          templates[templateMap[templateKey]] = template ? template.value : null;
        });
      }
      console.log(templates);
      return templates;
    },
  },
  methods: {
    onFinished() {
      this.hasSynced = true;
      if (this.canOnlySyncOneNetwork) {
        this.$emit('goNext', { sync: true });
      }
    }
  },
};
</script>

<style lang="scss">
.page.register-network {
  .register-icon {
    border-radius: 50%;
    height: 30px;
    width: 30px;
    min-width: 30px;
    color: white;
  }

  .register-text {
    h4 {
      font-weight: bold;
    }
    p {
      font-size: 1rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/onboarding/RegisterNetwork.vue